import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Message } from "@/store/zustand/supabaseStore";
import {
  CheckCircle,
  CircleIcon,
  File,
  Loader2,
  PlayIcon,
  Undo2Icon,
  XCircle,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useEditPrd } from "../../PRD/hooks/useEditPrd";
import { useApplyChatBlock } from "../useApplyChatBlock";
import { MessageSegment } from "../utils/messages";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useCanvasId } from "@/utils/utils";
import { FeaturePRD } from "@/types/issues";

export interface CommitProps {
  message: Message;

  block: MessageSegment;
  blockIndex: number;

  isLastBlock: boolean;
}

export const PRDBlock = React.memo(
  ({ message, block, blockIndex, isLastBlock }: CommitProps) => {
    const appliedBlock = message.message_context?.applied_segment_indices?.find(
      (idx: number) => idx === blockIndex,
    );

    const onEditPrd = useEditPrd();

    const { applyChatBlock, updoApplyChatBlock } = useApplyChatBlock();

    const canvasId = useCanvasId();
    const featurePrd = useIssuesStore(
      (state) => state.canvasIdToIssue[canvasId]?.featurePrd,
    );

    const [previousPrd, setPreviousPrd] = useState<string | undefined>();

    const setPrdRef = useRef(false);

    const [isLoading, setIsLoading] = useState(false);
    const [applied, setApplied] = useState(false);

    const hasApplied = applied || appliedBlock;

    useEffect(() => {
      if (message.message_context?.finished || !isLastBlock) {
        applyChatBlock(message, block, blockIndex, {
          previousPrd,
        });
        setIsLoading(false);
        setApplied(true);
      }
    }, [message.message_context?.finished, isLastBlock]);

    useEffect(() => {
      if (hasApplied || message.message_context?.finished || !isLastBlock) {
        setIsLoading(false);
        return;
      }

      // Auto-apply the block as the chat streams in
      if (block.parsedJson?.prd) {
        if (!setPrdRef.current) {
          // Save the previous PRD before it gets changed
          setPrdRef.current = true;
          setPreviousPrd(featurePrd?.prd ?? undefined);
        }

        onEditPrd("prd", block.parsedJson.prd, true);
        setIsLoading(true);
      }
    }, [block.parsedJson, hasApplied, featurePrd]);

    return (
      <div
        className={cn("flex flex-col grow bg-secondary-200 px-1 rounded-md")}
      >
        <div className="flex flex-row justify-between w-full items-center mt-1 px-0.5">
          <div
            className={
              "text-secondary-800 rounded-md text-2xs px-1.5 h-5 flex items-center grow-0 w-auto"
            }
          >
            <File className="text-secondary-n-600 h-4 w-4" />
          </div>
          <Button
            size="xs"
            variant="outline"
            className={cn("text-2xs h-6 px-1.5 gap-1")}
            disabled={!message.message_context?.finished}
            onClick={async () => {
              if (hasApplied) {
                updoApplyChatBlock(message, block, blockIndex);
                setApplied(false);
                return;
              }

              applyChatBlock(message, block, blockIndex);
              setApplied(true);
            }}
          >
            {isLoading ? (
              "Applying..."
            ) : hasApplied ? (
              <>
                <Undo2Icon className="w-2 h-2" />
                Undo
              </>
            ) : (
              <>
                <PlayIcon className="w-2 h-2" />
                Apply
              </>
            )}
          </Button>
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-start p-2">
            <div
              className={cn(
                "flex items-center justify-center",
                isLoading && "animate-spin",
              )}
            >
              {block.jsonIsBroken ? (
                <img
                  src="/icons/ai-chat-status/failed.svg"
                  className="size-4 shrink-0"
                />
              ) : hasApplied ? (
                <img
                  src="/icons/ai-chat-status/success.svg"
                  className="size-4 shrink-0"
                />
              ) : isLoading ? (
                <Loader2 className="size-4" />
              ) : (
                <CircleIcon className="size-4 shrink-0" />
              )}
            </div>

            <div
              className={cn(
                "rounded-md text-xs font-medium  py-0.5 px-1 text-secondary-n-50 break-all",
              )}
            >
              {block.jsonIsBroken
                ? "Invalid PRD From AI. Please Try Again."
                : "New PRD"}
            </div>
          </div>
        </div>
      </div>
    );
  },
);
